// Orchestration for the project objective page with tabs:
// - Details
// - Performance
// - Roster

import { Layout, Tabs, Button, Typography } from "antd";
import { useState, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ReloadOutlined } from "@ant-design/icons";
import ProjectObjectiveDetails from "./ProjectObjectiveDetails";
import ProjectObjectiveHeader from "./ProjectObjectiveHeader";
import CloneProjectObjectiveModal from "./CloneProjectObjectiveModal";
import { colors } from "../../config/constants";
import PerformanceOrchestrator from "../performance/PerformanceOrchestrator";
import RosterOrchestrator from "../roster/RosterOrchestrator";
import {
  DashboardOutlined,
  InfoCircleOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import TimeframeSelector from "../performance/TimeframeSelector";
import { getDefaultDateRange } from "../../utils/formatters";

const { Text } = Typography;

const ProjectObjectiveViewOrchestrator = () => {
  const { projectObjectiveId: poId } = useParams();
  const [searchParams] = useSearchParams();
  const view = searchParams.get("view") || "details";
  const [activeTab, setActiveTab] = useState(view);
  const [timeframeDateRange, setTimeframeDateRange] =
    useState(getDefaultDateRange);
  const [objectiveMetadata, setObjectiveMetadata] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [headerState, setHeaderState] = useState({
    saving: false,
    hasFormErrors: false,
    validating: false,
  });

  const detailsHandlersGetterRef = useRef(null);
  const rosterOrchestratorRef = useRef(null);
  const [rosterLastSyncedAt, setRosterLastSyncedAt] = useState(null);

  const handleEdit = useCallback(() => setIsEditing(true), []);
  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    detailsHandlersGetterRef.current?.().resetEdit?.();
  }, []);
  const handleSave = useCallback(() => {
    detailsHandlersGetterRef.current?.().handleSave?.();
  }, []);
  const handleValidate = useCallback(() => {
    detailsHandlersGetterRef.current?.().handleValidate?.();
  }, []);
  const handleClone = useCallback(() => setShowCloneModal(true), []);
  const handleTogglePreview = useCallback(() => setShowPreview((v) => !v), []);

  const registerDetailsHandlers = useCallback((getter) => {
    detailsHandlersGetterRef.current = getter;
  }, []);
  const handleTimeframeDateRangeChange = useCallback((start, end) => {
    setTimeframeDateRange({ start, end });
  }, []);

  const getTabActions = () => {
    switch (activeTab) {
      case "performance":
        return (
          <TimeframeSelector
            onDateRangeChange={handleTimeframeDateRangeChange}
          />
        );
      default:
        return;
    }
  };

  return (
    <Layout>
      <ProjectObjectiveHeader
        objectiveName={objectiveMetadata?.name || "Unnamed Objective"}
        isEditing={isEditing}
        saving={headerState.saving}
        hasFormErrors={headerState.hasFormErrors}
        validating={headerState.validating}
        showPreview={showPreview}
        onEdit={handleEdit}
        onCancelEdit={handleCancelEdit}
        onSave={handleSave}
        onValidate={handleValidate}
        onClone={handleClone}
        onTogglePreview={handleTogglePreview}
      />
      <Layout.Content style={{ position: "relative" }}>
        <Tabs
          defaultActiveKey={view}
          size="medium"
          styles={{
            header: {
              padding: "0 16px 0 32px",
              position: "sticky",
              top: 72,
              zIndex: 25,
              backgroundColor: colors.white,
              marginBottom: 0,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            },
          }}
          tabBarExtraContent={getTabActions()}
          onChange={(key) => setActiveTab(key)}
          activeKey={activeTab}
          items={[
            {
              key: "details",
              label: "Details",
              icon: <InfoCircleOutlined />,
              children: (
                <div className="mx-auto px-4 sm:px-6 lg:px-8">
                  <ProjectObjectiveDetails
                    projectObjectiveId={poId}
                    setObjectiveMetadata={setObjectiveMetadata}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    showCloneModal={showCloneModal}
                    setShowCloneModal={setShowCloneModal}
                    showPreview={showPreview}
                    setShowPreview={setShowPreview}
                    onHeaderStateChange={setHeaderState}
                    onRegisterHandlers={registerDetailsHandlers}
                  />
                </div>
              ),
            },
            {
              key: "performance",
              label: "Performance",
              icon: <DashboardOutlined />,
              children: (
                <PerformanceOrchestrator
                  objectiveId={poId}
                  timeframeDateRange={timeframeDateRange}
                />
              ),
            },
            {
              key: "roster",
              label: "Roster",
              icon: <TeamOutlined />,
              children: (
                <>
                  <div
                    style={{
                      marginBottom: 12,
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => rosterOrchestratorRef.current?.refresh?.()}
                    >
                      Refresh
                    </Button>
                    <Text type="secondary">
                      {rosterLastSyncedAt
                        ? `Last refreshed at ${rosterLastSyncedAt.toLocaleString()}`
                        : "No data in cache. Click Refresh to load."}
                    </Text>
                  </div>
                  <RosterOrchestrator
                    ref={rosterOrchestratorRef}
                    objectiveId={poId}
                    onLastSyncedAtChange={setRosterLastSyncedAt}
                  />
                </>
              ),
            },
          ]}
        />
        <CloneProjectObjectiveModal
          visible={showCloneModal}
          onClose={() => setShowCloneModal(false)}
          onSuccess={() => setShowCloneModal(false)}
          objectiveId={poId}
          objectiveName={objectiveMetadata?.name}
        />
      </Layout.Content>
    </Layout>
  );
};

export default ProjectObjectiveViewOrchestrator;
